import type { NurseRole, RatioLadder } from "@/utils/models";

/** Who a ratio location's ladder rows belong to: the location's roles (as ladder rows name them) and its job. */
export type LadderOwner = { roles: readonly NurseRole[]; jobId: string };

/**
 * Keeps the ladder's rows owned by the location when its job or roles change. `null` when nothing that
 * matters changed, so the caller leaves the ladder (and the form's "changed" state) alone.
 *
 * - Job management on: only the job matters, and a new job is written onto every row.
 * - Otherwise only the roles matter, compared as sets. With one role or none, every row takes the
 *   location's roles. With several, each row keeps the ones the location still has — or all of the
 *   location's roles when none of its own are left.
 */
export const syncLadderOwner = (ladder: RatioLadder[], prev: LadderOwner, next: LadderOwner, jobManagementEnabled: boolean): RatioLadder[] | null => {
	if (jobManagementEnabled) {
		if (prev.jobId === next.jobId) return null;
		return ladder.map((entry) => ({
			...entry,
			jobId: next.jobId,
		}));
	}
	const prevSet = new Set(prev.roles.map(String));
	const currSet = new Set(next.roles.map(String));
	if (prevSet.size === currSet.size && [...prevSet].every((r) => currSet.has(r))) return null;

	if (next.roles.length <= 1) {
		return ladder.map((entry) => ({
			...entry,
			roles: [...next.roles],
		}));
	}
	return ladder.map((entry) => {
		const filtered = entry.roles.filter((r) => currSet.has(String(r)));
		return {
			...entry,
			roles: filtered.length > 0 ? filtered : [...next.roles],
		};
	});
};
